import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { PatrolAbsorber, UpdatePatrolAbsorberBody } from "../../../../../http/patrol/types/absorbers";
import { useAbsorbersApi } from "../../../../../http/patrol/useAbsorbers";
import { usePatrolSteelVersionsApi } from "../../../../../http/patrol/useSteelVersion";
import { AdminCommonSteelVersion } from "../../../../../http/types/commonSteelVersions";
import { SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Numeric from "../../../../ui/Numeric";
import { CustomAsyncSelect, CustomSelect } from "../../../../ui/select/CustomSelect";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface AbsorberFormBody {
  item: SearchAdminItem | null;
  material: AdminCommonSteelVersion | null;
  order: number | null;
  visibility: VisibilityScopeValue;
}

interface AbsorberFormProps {
  defaultValues?: PatrolAbsorber;
  onClose: () => void;
}

const AbsorberForm: FC<AbsorberFormProps> = ({ onClose, defaultValues }) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);

  const itemsApi = useItemsApi();
  const absorbersApi = useAbsorbersApi();
  const materialApi = usePatrolSteelVersionsApi();

  const materialResult = materialApi.useAdminMaterials({}, aborter.current.signal);

  return (
    <Form<AbsorberFormBody>
      defaultValues={{ item: null, order: 50, visibility: "hidden" }}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting}
      secondaryButton={
        <Button buttonProps={{ onClick: onClose }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={() => async (formData) => {
        const common: UpdatePatrolAbsorberBody = {
          materialId: formData.material?.id ?? "",
          order: formData.order!,
          visibility: formData.visibility,
        };
        if (defaultValues?.item.id)
          try {
            await absorbersApi.updateAdminAbsorber(defaultValues.item.id, common);
            onClose();
          } catch (err) {
            dispatch(showErrorNotification(i18n, err));
          } finally {
            setIsSubmitting(false);
          }
        else
          try {
            await absorbersApi.insertAdminAbsorber({
              ...common,
              itemId: formData.item?.id ?? "",
            });
            onClose();
          } catch (err) {
            dispatch(showErrorNotification(i18n, err));
          } finally {
            setIsSubmitting(false);
          }
      }}
    >
      {({ control, setValue, reset }) => {
        useEffect(() => {
          defaultValues && reset(defaultValues);
        }, [defaultValues?.item.id]);

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                      {({ labelId }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef(new AbortController());

                        const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                        // Fetch initial data on mount
                        useEffect(() => {
                          loadOptions(searchTerm.current).then(setDefaultOptions);
                        }, []);

                        return (
                          <CustomAsyncSelect
                            {...field}
                            inputId={labelId}
                            defaultOptions={defaultOptions}
                            loadOptions={loadOptions}
                            isClearable
                            getOptionLabel={(x) => x.code}
                            isDisabled={!!defaultValues?.item.id}
                            getOptionValue={(x) => x.id}
                            onChange={(value) => setValue("item", value ?? null)}
                          />
                        );
                      }}
                    </FormField>
                  </>
                );
              }}
            />
            <Controller
              name="material"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.materials.singular} error={fieldState.error}>
                  {({ labelId }) => (
                    <CustomSelect
                      {...field}
                      inputId={labelId}
                      options={materialResult.data?.items ?? []}
                      isDisabled={!!materialResult.error || isSubmitting}
                      isLoading={materialResult.isLoading}
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default AbsorberForm;
